import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { Button, Checkbox, FormGroup, Input, Select } from "@cofano/cck";
import FormField from "components/cck/FormField";
import { FormDialog } from "components/dialogs/Dialogs";
import AvailableShipSelect, { AvailableShipOption } from "components/selects/AvailableShipSelect";
import VoyageSelect, { VoyageSelectOption } from "components/selects/VoyageSelect";
import { useConfirm } from "components/hooks/useConfirm";
import useCreateOrExistingCharterFormFields, {
	charterDefaultValues,
	CreateOrExistingPurchaseCharterForm,
	ExistingCharterType,
} from "view/charters/components/form/useCreateOrExistingCharterFormFields";
import { hasPluginPermission, Language } from "imports/Utils";
import { PlanVoyageInput } from "__generated__/graphql";
import { Voyage } from "types/index";
import { ChildProps } from "types/commons";
import { ShipLinkDialogSalesCharterFragment } from "__generated__/view/charters/graphql/charter.fragments.generated";
import { canPlanOnExistingArea, getAgreementShipFromSalesCharter } from "view/charters/dossier/utils";
import {
	ChangeOperatorForShipDocument,
	ShipLinkDialogPlanVoyageDocument,
	ShipLinkDialogPlanVoyageMutation,
} from "__generated__/view/charters/graphql/charter.mutations.generated";
import { calculateMaxWeightAndMaxVolume } from "view/cargos/utils/CargoUtils";
import { ShipPlanningUnplanCargosDocument } from "__generated__/view/shipPlanning/graphql/shipplanning.mutations.generated";
import PlanningChecks from "components/planningChecks/PlanningChecks";
import { useStoreState } from "ezStore";

type QueriedVoyage = ChildProps<ShipLinkDialogPlanVoyageMutation, "planVoyage">;

enum VoyageType {
	New = "NEW",
	Existing = "EXISTING"
}

type ShipLinkDialogForm = {
	ship?: AvailableShipOption | null;
	voyage?: VoyageSelectOption;
	voyageNumber?: string;
	planOnExistingArea?: boolean;
} & CreateOrExistingPurchaseCharterForm;

type BaseProps = {
	open: boolean;
	onClose: () => void;
	charter: ShipLinkDialogSalesCharterFragment;
	existingVoyage?: Pick<Voyage, "id" | "voyageNumber" | "from" | "to"> & {
		ship?: AvailableShipOption["ship"] | null;
		charter?: ExistingCharterType | null;
	};
	resetShipNomination?: boolean;
}

type DefaultProps = {
	onCompleted: (result: QueriedVoyage) => void;
	overrideSaveMutation?: never;
}

type OverrideProps<TResult,> = {
	onCompleted: (result: NonNullable<TResult>) => void;
	overrideSaveMutation: {
		overrideFunction: (input: PlanVoyageInput) => Promise<TResult>,
		loading: boolean
	};
}

export function ShipLinkDialog(props: BaseProps & DefaultProps): React.ReactElement;
export function ShipLinkDialog<TResult,>(props: BaseProps & OverrideProps<TResult>): React.ReactElement;

export function ShipLinkDialog<TResult,>({
	open,
	onClose,
	onCompleted,
	charter,
	existingVoyage,
	resetShipNomination = false,
	overrideSaveMutation,
}: BaseProps & (DefaultProps | OverrideProps<TResult>)) {

	const session = useStoreState(store => store.session);
	const confirm = useConfirm();

	const [ voyageType, setVoyageType ] = useState(existingVoyage ? VoyageType.Existing : VoyageType.New);
	const [ shipId, setShipId ] = useState<number | undefined>(undefined);

	const cargos = charter.cargos.sort((first, second) => {
		return (first.firstLoadingAction?.start ?? 0) - (second.firstLoadingAction?.start ?? 0);
	});

	const [ maxWeight, maxVolume ] = calculateMaxWeightAndMaxVolume([ ...cargos ].flatMap(c => c.actions));

	const defaultValues: ShipLinkDialogForm = {
		ship: existingVoyage?.ship
			? { ship: existingVoyage.ship, isAvailable: true, hasActiveTimeCharter: false }
			: null,
		voyage: existingVoyage,
		voyageNumber: existingVoyage ? existingVoyage.voyageNumber : "",
		charter: existingVoyage?.charter ? {
			...charterDefaultValues,
			subtype: "EXISTING" as const,
			existing: existingVoyage.charter,
		} : charterDefaultValues,
		planOnExistingArea: false,
	};

	const { control, handleSubmit, formState, reset, watch, setValue } = useForm<ShipLinkDialogForm>({
		defaultValues: defaultValues,
	});

	const [ voyage, ship ] = watch([ "voyage", "ship" ]);

	const { charterInput, start, end, charterFields, createContract } = useCreateOrExistingCharterFormFields({
		control: control,
		setValue: setValue,
		watch: watch,
		formState: formState,
		ship: ship?.ship ?? undefined,
		voyage: voyage,
		cargos: charter.cargos,
		createContractDisabled: false,
	});

	const [ updateShipOperator ] = useMutation(ChangeOperatorForShipDocument);
	const [ unplanVoyage ] = useMutation(ShipPlanningUnplanCargosDocument);
	const [ planVoyage, { loading: loading } ] = useMutation(ShipLinkDialogPlanVoyageDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
		setVoyageType(VoyageType.New);
	};

	const linkShip = async (data: ShipLinkDialogForm) => {
		if (existingVoyage) {
			await unplanVoyage({
				variables: {
					salesCharterId: charter.id,
					voyageId: existingVoyage!.id,
					deleteCharter: true,
					deleteVoyage: true,
				},
			});
		}

		const input: PlanVoyageInput = {
			salesCharterIds: [ charter.id ],
			voyageInput: {
				id: data.voyage?.id,
				voyageNumber: data.voyageNumber,
				creditor: charterInput.creditor ? {
					id: charterInput.creditor?.id,
				} : undefined,
				shipId: data.ship?.ship.id,
			},
			charterInput: charterInput,
			cargoIds: [ ...cargos ].map(c => c.id),
			planOnExistingArea: canPlanOnExistingArea(voyage, cargos) ? data.planOnExistingArea : false,
			createContract: createContract,
			resetShipNomination: resetShipNomination,
		};

		if (overrideSaveMutation) {
			const result = await overrideSaveMutation.overrideFunction(input);
			if (result) {
				onCompleted(result);
			}
		} else {
			const result = await planVoyage({
				variables: {
					planVoyageInput: input,
				},
			});
			if (result.data?.planVoyage) {
				onCompleted(result.data?.planVoyage);
			}
		}

		onClose();
	};

	const save = (data: ShipLinkDialogForm) => {
		if (charterInput.creditor?.id && (charterInput.creditor.id !== data.ship?.ship.operator?.id) && (charterInput.creditor.id !== data.ship?.ship.owner?.id)) {
			confirm({
				type: "WARNING",
				title: intl.formatMessage({ id: "CONFIRM_CHANGING_OPERATOR_IN_MASTERDATA" }),
				confirmText: intl.formatMessage({ id: "YES" }),
				secondConfirmButton: (onClose) => (
					<Button
						onClick={async () => {
							onClose();
							await linkShip(data);
						}}
						size="small">
						{intl.formatMessage({ id: "NO" })}
					</Button>
				),
			}).then(() => {
				if (data.ship?.ship.id && charterInput.creditor?.id) {
					updateShipOperator({
						variables: {
							shipId: data.ship.ship.id,
							operatorId: charterInput.creditor.id,
						},
					}).then(async () => {
						await linkShip(data);
					});
				}
			});
		} else {
			linkShip(data);
		}
	};

	useEffect(() => {
		const ship = getAgreementShipFromSalesCharter(charter);
		if (ship != null) {
			setShipId(ship.id);
		}
	}, [ charter ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "OPERATOR_LINK_SHIP" })}
			form="shipLinkForm"
			loading={loading || overrideSaveMutation?.loading}
		>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="shipLinkForm">
				{hasPluginPermission(session, "angler") &&
					<PlanningChecks shipId={ship?.ship.id} cargoIds={cargos.map(x => x.id)} />}
				<FormGroup
					label={Language("SHIP")}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<FormField errors={formState.errors} name="ship">
						<Controller
							name="ship"
							control={control}
							rules={{ required: true }}
							render={({ field }) => (
								<AvailableShipSelect
									{ ...field}
									shipId={shipId}
									availabilityProperties={{
										capacityTon: maxWeight,
										capacityM3: maxVolume,
										window: {
											from: start,
											to: end,
										},
									}}
									disableClear
									onChange={(ship) => {
										field.onChange(ship || null);
										setVoyageType(VoyageType.New);
										setValue("voyage", undefined);
										setValue("voyageNumber", "");
									} }
								/>
							)} />
					</FormField>
				</FormGroup>

				<FormGroup label={Language("VOYAGE")} labelPlacement="left-center" overflow="hidden">
					<Select
						value={voyageType}
						fetchOptions={Object.values(VoyageType)}
						onChange={option => {
							setVoyageType(option || VoyageType.New);
							setValue("voyage", undefined);
							setValue("voyageNumber", undefined);
							if (option === VoyageType.New) {
								setValue("voyageNumber", "");
							}
						}}
						disabled={!ship}
						getOptionLabel={option => intl.formatMessage({ id: option })}
						clearable={false}
						disableSearch
					/>
				</FormGroup>

				{voyageType === VoyageType.Existing && (
					<FormGroup
						label={Language("VOYAGE")}
						labelPlacement="left-center"
						overflow="hidden"
						required>
						<FormField errors={formState.errors} name={"voyage"}>
							<Controller
								name="voyage"
								control={control}
								rules={{ required: true }}
								render={({ field }) => (
									<VoyageSelect
										{...field}
										searchableShipId={ship?.ship.id}
										onChange={option => {
											field.onChange(option || undefined);
											setValue("voyageNumber", option?.voyageNumber);
										}}
									/>
								)}
							/>
						</FormField>
					</FormGroup>
				)}

				{voyageType === VoyageType.New && (
					<FormGroup
						label={Language("VOYAGE_NUMBER")}
						labelPlacement="left-center"
						tooltip={intl.formatMessage({ id: "VOYAGE_NUMBER_TIPS" })}
						overflow="hidden">
						<Controller
							name="voyageNumber"
							control={control}
							disabled={!!voyage}
							render={({ field }) => <Input {...field} />}
						/>
					</FormGroup>
				)}

				<h4 style={{ fontSize: 16, fontWeight: 600, margin: "24px 0 16px" }}>
					{intl.formatMessage({ id: "CHARTER" })}
				</h4>

				{charterFields}

				{canPlanOnExistingArea(voyage, cargos) && (
					<div style={{ marginTop: 40 }}>
						<FormGroup label={intl.formatMessage({ id: "CARGOS_COULD_BE_PLACED_ON_EXISTING_CALLS" })} overflow="visible">
							<Controller
								name="planOnExistingArea"
								control={control}
								render={({ field }) => (
									<Checkbox {...field} value={undefined} checked={field.value} />
								)}
							/>
						</FormGroup>
					</div>
				)}
			</form>
		</FormDialog>
	);
}
